import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  AddOutlined as AddIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  FileUploadOutlined as ImportIcon,
  IosShareOutlined as ExportIcon,
  DownloadOutlined as DownloadIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../apiBase';

export default function TransactionTypeManagement() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [query, setQuery] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ transactionType: '' });
  const [deleteId, setDeleteId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const fileRef = useRef(null);

  const loadRows = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/transaction-types`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      });
      const data = await res.json().catch(() => ([]));
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setToast({ open: true, message: e?.message || 'Failed to load', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  const filteredRows = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.transactionType, r.createdAt].some((v) => String(v || '').toLowerCase().includes(q)));
  }, [rows, query]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const openAdd = () => {
    setEditingId(null);
    setForm({ transactionType: '' });
    setEditOpen(true);
  };
  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({ transactionType: row.transactionType || '' });
    setEditOpen(true);
  };

  const save = async () => {
    const body = { ...form };
    if (!body.transactionType) {
      setToast({ open: true, message: 'Transaction type is required', severity: 'error' });
      return;
    }
    setLoading(true);
    try {
      if (editingId) {
        const res = await fetch(`${API_BASE_URL}/api/transaction-types/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
          body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'Update failed');
        setToast({ open: true, message: 'Updated', severity: 'success' });
      } else {
        const res = await fetch(`${API_BASE_URL}/api/transaction-types`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
          body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'Create failed');
        setToast({ open: true, message: 'Added', severity: 'success' });
      }
      setEditOpen(false);
      setEditingId(null);
      await loadRows();
    } catch (e) {
      setToast({ open: true, message: e?.message || 'Action failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (id) => {
    setDeleteId(id);
    setDeleteOpen(true);
  };
  const doDelete = async () => {
    if (!deleteId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/transaction-types/${deleteId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Delete failed');
      setRows((prev) => prev.filter((r) => r.id !== deleteId));
      setToast({ open: true, message: 'Deleted', severity: 'info' });
    } catch (e) {
      setToast({ open: true, message: e?.message || 'Delete failed', severity: 'error' });
    } finally {
      setDeleteOpen(false);
      setDeleteId(null);
      setLoading(false);
    }
  };

  const exportCsv = () => {
    const header = ['Transaction Type', 'Created At'];
    const data = rows.map((r) => [r.transactionType, r.createdAt]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'transaction_types.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadTemplate = () => {
    const header = ['Transaction Type'];
    const ws = XLSX.utils.aoa_to_sheet([header, ['Cash']]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'transaction_types_template.xlsx');
  };

  const importFile = async (file) => {
    if (!file) return;
    setLoading(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { header: 0, raw: false, defval: '' });
      const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const items = json
        .map((r) => {
          const km = {};
          Object.keys(r).forEach((k) => (km[norm(k)] = r[k]));
          const transactionType = String(km[norm('Transaction Type')] || km['transactiontype'] || km['name'] || '').trim();
          if (!transactionType) return null;
          return { transactionType };
        })
        .filter(Boolean);
      if (items.length === 0) throw new Error('No valid rows found');
      const res = await fetch(`${API_BASE_URL}/api/transaction-types/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        body: JSON.stringify({ items }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Import failed');
      setToast({
        open: true,
        message: `Import completed: ${data?.inserted ?? 0} added, ${data?.skipped ?? 0} skipped`,
        severity: 'success',
      });
      await loadRows();
    } catch (e) {
      setToast({ open: true, message: e?.message || 'Import failed', severity: 'error' });
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Layout title="Transaction Type Management">
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Typography variant="h4" align="center" sx={{ fontWeight: 500, letterSpacing: 0.5, mb: 3 }}>
          Transaction Type Management
        </Typography>

        <Stack direction="row" spacing={1.5} sx={{ mb: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button startIcon={<ExportIcon />} variant="contained" color="success" onClick={exportCsv} disabled={loading}>
            Export
          </Button>
          <Button startIcon={<DownloadIcon />} variant="contained" color="info" onClick={downloadTemplate} disabled={loading}>
            Download Template
          </Button>
          <Button variant="contained" component="label" disabled={loading}>
            Import
            <input hidden ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={(e) => importFile(e.target.files?.[0])} />
          </Button>
          <Button startIcon={<AddIcon />} variant="contained" onClick={openAdd}>
            Add
          </Button>
        </Stack>

        <Paper sx={{ p: 2, borderRadius: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems="center">
            <TextField
              size="small"
              placeholder="Search transaction type…"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: { xs: '100%', md: 320 } }}
            />
            {query.trim() && (
              <Button
                variant="text"
                color="inherit"
                onClick={() => {
                  setQuery('');
                  setPage(0);
                }}
              >
                Clear
              </Button>
            )}
            <Chip
              size="small"
              label={rows.length === filteredRows.length ? `${rows.length} total` : `Showing ${filteredRows.length} of ${rows.length}`}
            />
          </Stack>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Sr No</TableCell>
                  <TableCell>Transaction Type</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.map((r, idx) => (
                  <TableRow key={r.id}>
                    <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{r.transactionType}</TableCell>
                    <TableCell>{r.createdAt ? String(r.createdAt).slice(0, 16).replace('T', ' ') : '-'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(r)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => confirmDelete(r.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>

        <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 800 }}>{editingId ? 'Edit' : 'Add'} Transaction Type</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <TextField
                label="Transaction Type"
                value={form.transactionType}
                onChange={(e) => setForm((p) => ({ ...p, transactionType: e.target.value }))}
                placeholder="e.g., Cash, Bank, Contra"
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setEditOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button onClick={save} variant="contained" disabled={loading}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 800 }}>Delete Record?</DialogTitle>
          <DialogContent>
            <Typography sx={{ color: 'text.secondary' }}>This action cannot be undone.</Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDeleteOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button onClick={doDelete} variant="contained" color="error" disabled={loading}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={toast.open}
          autoHideDuration={2500}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={() => setToast((t) => ({ ...t, open: false }))} severity={toast.severity} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
}
